import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { MaskedLines, FadeUp, Eyebrow } from "../components/Reveal";
import { IMG } from "./HomePage";

const HIGHLIGHTS = [
  { n: "01", t: "Programmazione degli interventi", d: "Manutenzioni pianificate nel tempo, con calendario condiviso e scadenze gestite." },
  { n: "02", t: "Gestione di più unità immobiliari", d: "Interventi coordinati su più appartamenti, condomini e stabili dello stesso mandato." },
  { n: "03", t: "Rapporti finali", d: "Ogni intervento si conclude con un rapporto documentato del lavoro eseguito." },
  { n: "04", t: "Identificazione degli impianti", d: "Ogni impianto viene registrato con le sue caratteristiche, per interventi futuri più rapidi." },
  { n: "05", t: "Storico delle manutenzioni", d: "Le lavorazioni eseguite restano tracciate nel tempo, impianto per impianto." },
  { n: "06", t: "Preventivi per interventi multipli", d: "Per più unità o impianti condominiali prepariamo un preventivo dedicato e trasparente." },
];

export default function AmministrazioniPage() {
  return (
    <div data-testid="amministrazioni-page">
      <Seo
        title="Manutenzioni per amministrazioni immobiliari | V32 Group — Ticino"
        description="Manutenzioni tecniche programmate per amministrazioni immobiliari in Ticino: pulizia boiler, lavaggio serpentine e filtri acqua su più unità, con rapporti documentati e prezzi trasparenti."
      />

      <section className="pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Eyebrow>Per amministrazioni immobiliari</Eyebrow>
          <MaskedLines
            lines={["Manutenzioni programmate per", "amministrazioni immobiliari."]}
            delay={0.15}
            className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-ink max-w-3xl"
          />
          <FadeUp delay={0.5}>
            <p className="mt-8 text-ink/70 text-base md:text-lg leading-relaxed max-w-2xl">
              V32 Group supporta le amministrazioni immobiliari nella gestione delle
              manutenzioni tecniche programmate, con interventi organizzati, prezzi
              trasparenti e rapporti documentati.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <FadeUp>
            <div className="img-frame aspect-[21/9] max-h-[480px] w-full">
              <img
                src={IMG.buildings}
                alt="Stabile residenziale moderno gestito da un'amministrazione immobiliare in Ticino"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </FadeUp>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {HIGHLIGHTS.map((h, i) => (
              <FadeUp key={h.n} delay={0.05 * i} className="bg-avorio">
                <div className="p-8 h-full hover:bg-beige transition-colors duration-300" data-testid={`highlight-${h.n}`}>
                  <p className="font-mono text-xs text-bronzo tracking-[0.25em]">{h.n}</p>
                  <h2 className="mt-4 font-display font-medium text-lg text-ink">{h.t}</h2>
                  <p className="mt-3 text-sm text-ink/60 leading-relaxed">{h.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-beige">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <FadeUp>
            <Eyebrow>Come lavoriamo con voi</Eyebrow>
            <h2 className="mt-6 font-display font-normal text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink text-balance">
              Un referente tecnico per il patrimonio che gestite.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="space-y-6">
              {[
                "Ci indicate gli stabili e gli impianti da mantenere.",
                "Definiamo insieme programma, priorità e preventivo.",
                "Eseguiamo gli interventi coordinando accessi e tempistiche con gli inquilini.",
                "Ricevete un rapporto documentato per ogni impianto, con storico e raccomandazioni.",
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <span className="font-mono text-xs text-bronzo tracking-[0.2em] mt-1 shrink-0">0{i + 1}</span>
                  <p className="text-ink/70 leading-relaxed text-sm">{step}</p>
                </div>
              ))}
              <Link
                to="/contatti?cliente=amministrazione"
                data-testid="amministrazioni-cta"
                className="mt-4 inline-flex items-center gap-2 bg-antracite text-avorio font-medium px-8 py-4 hover:bg-bronzo transition-colors duration-300"
              >
                Richiedi una proposta per i tuoi immobili <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
